import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import type { PaddingOptions } from 'maplibre-gl';
import { MapPreviewPicker } from '@/components/map/MapPreviewPicker';
import type { MapPreviewPickerMapOptions } from '@/components/map/MapPreviewPicker';
import type { BasemapId } from '@/config/maplibre';
import {
  MAP_SETUP_FLY_TO_DURATION_MS,
  MAP_SETUP_ZOOM_EASE_MS,
} from '@/constants';
import type { Coordinates } from '@/types';
import { MapConfigDragHint } from './MapConfigDragHint';
import { MapConfigZoomControls } from './MapConfigZoomControls';
import styles from './MapConfigPreview.module.css';

interface MapConfigPreviewProps {
  /** Live map center (draggable), or `null` while unset. */
  center: Coordinates | null;
  /** Start zoom — drives the map live as the slider moves. */
  startZoom: number;
  /** Minimum reachable zoom. */
  minZoom: number;
  /** Maximum reachable zoom. */
  maxZoom: number;
  /** Default basemap whose style the preview renders. */
  defaultBasemapId: BasemapId;
  /**
   * MapLibre camera padding that frames the center coordinate in the visible
   * map area (left of the panel / above the mobile sheet). Owned by the page
   * orchestrator so this memoized preview stays presentational.
   */
  padding: PaddingOptions;
  /** Called when the marker is dragged to a new center. */
  onCenterChange: (coords: Coordinates) => void;
}

/**
 * Lightweight live map preview for the configurator (no tasks / notes /
 * drawing). Mouse wheel and +/- buttons zoom freely; the form sliders steer the
 * map one-way (zoom bounds + start zoom); the country pick / marker drag move
 * the center; the default basemap selection swaps the rendered style.
 *
 * Reuses the shared `MapPreviewPicker` for the `<Map>` + draggable
 * `AnchorMarker` core, and layers the configurator-specific imperative camera
 * control (start-zoom easing, recenter fly-to), zoom controls, and drag hint on
 * top.
 */
export const MapConfigPreview = memo(function MapConfigPreview({
  center,
  startZoom,
  minZoom,
  maxZoom,
  defaultBasemapId,
  padding,
  onCenterChange,
}: MapConfigPreviewProps) {
  const mapRef = useRef<MapRef | null>(null);
  const [isMapReady, setIsMapReady] = useState(false);
  const [hasDraggedCenter, setHasDraggedCenter] = useState(false);
  const suppressRecenterRef = useRef(false);

  const handleMapLoad = useCallback(() => setIsMapReady(true), []);

  const handleDragEnd = useCallback(
    (coords: Coordinates) => {
      suppressRecenterRef.current = true;
      setHasDraggedCenter(true);
      onCenterChange(coords);
    },
    [onCenterChange]
  );

  // Start-zoom slider drives the map live (one-way). Min/max bounds are applied
  // reactively through the `minZoom`/`maxZoom` props on <Map>.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady) return;
    map.easeTo({ zoom: startZoom, duration: MAP_SETUP_ZOOM_EASE_MS });
  }, [startZoom, isMapReady]);

  // Country pick / programmatic recenter flies to the new center (skipped right
  // after a marker drag, where the map is already at the right place).
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !isMapReady || !center) return;
    if (suppressRecenterRef.current) {
      suppressRecenterRef.current = false;
      return;
    }
    map.flyTo({
      center: [center.lng, center.lat],
      duration: MAP_SETUP_FLY_TO_DURATION_MS,
    });
  }, [center, isMapReady]);

  const mapOptions = useMemo<MapPreviewPickerMapOptions>(
    () => ({
      minZoom,
      maxZoom,
      scrollZoom: true,
      doubleClickZoom: true,
      dragPan: true,
      styleDiffing: false,
      attributionControl: { compact: true },
      padding,
    }),
    [minZoom, maxZoom, padding]
  );

  return (
    <MapPreviewPicker
      center={center}
      zoom={startZoom}
      basemapId={defaultBasemapId}
      onCenterChange={handleDragEnd}
      mapRef={mapRef}
      onLoad={handleMapLoad}
      markerVisible={isMapReady && center !== null}
      mapOptions={mapOptions}
      className={styles.preview}
    >
      <MapConfigZoomControls
        mapRef={mapRef}
        isMapReady={isMapReady}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />

      {isMapReady && center !== null && !hasDraggedCenter && (
        <MapConfigDragHint />
      )}
    </MapPreviewPicker>
  );
});
